import React from "react";
import { Check, FileText, Info } from "lucide-react";

// Left-hand progress card: answered progress, numbered question tiles and the
// route to the review overview. Answered (content) and save problems are
// shown separately: a tile can be answered but not yet saved.
const AssessmentNavigator = ({
  questions,
  currentIndex,
  answeredIds,
  saveStates,
  reviewing,
  onSelect,
  onReview,
}) => {
  const total = questions.length;
  const answered = questions.filter((question) => answeredIds.has(question.id)).length;
  const percent = total > 0 ? Math.round((answered / total) * 100) : 0;

  return (
    <aside className="eq-assess-nav" aria-label="Assessment progress">
      <h2 className="eq-assess-nav-title">Your progress</h2>
      <p className="eq-assess-progress-text">{answered} of {total} answered</p>
      <div
        className="eq-assess-progress"
        role="progressbar"
        aria-label="Questions answered"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={answered}
      >
        <span style={{ width: `${percent}%` }} />
      </div>

      <h3 className="eq-assess-nav-subtitle" id="eq-assess-questions-heading">Questions</h3>
      <ol className="eq-assess-grid" aria-labelledby="eq-assess-questions-heading">
        {questions.map((question, index) => {
          const isCurrent = !reviewing && index === currentIndex;
          const isAnswered = answeredIds.has(question.id);
          const state = saveStates[question.id];
          const needsAttention = state === "failed" || state === "conflict";
          const label = [
            `Question ${index + 1}`,
            isAnswered ? "answered" : "unanswered",
            isCurrent && "current",
            state === "failed" && "not saved",
            state === "conflict" && "changed in another window",
          ].filter(Boolean).join(", ");
          return (
            <li key={question.id}>
              <button
                type="button"
                className={[
                  "eq-assess-tile",
                  isCurrent && "is-current",
                  isAnswered && "is-answered",
                  needsAttention && "needs-attention",
                ].filter(Boolean).join(" ")}
                aria-current={isCurrent ? "step" : undefined}
                aria-label={label}
                onClick={() => onSelect(index)}
              >
                <span>{index + 1}</span>
                {isAnswered && !isCurrent && <Check className="eq-assess-tile-check" size={15} aria-hidden="true" />}
                {needsAttention && <i className="eq-assess-tile-flag" aria-hidden="true">!</i>}
              </button>
            </li>
          );
        })}
      </ol>

      <ul className="eq-assess-legend" aria-hidden="true">
        <li><i className="is-answered" />Answered</li>
        <li><i className="is-current" />Current</li>
        <li><i className="is-unanswered" />Unanswered</li>
      </ul>

      <p className="eq-assess-nav-note">
        <Info size={18} aria-hidden="true" />
        <span>You can return to any question before submitting.</span>
      </p>

      <button
        type="button"
        className={`eq-assess-review-btn${reviewing ? " is-active" : ""}`}
        onClick={onReview}
        aria-pressed={reviewing}
      >
        <FileText size={19} aria-hidden="true" />Review answers
      </button>
    </aside>
  );
};

export default AssessmentNavigator;
